import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Layout, LayoutContent, LayoutHeader } from '@astryxdesign/core/Layout'
import { Stack } from '@astryxdesign/core/Stack'
import { Center } from '@astryxdesign/core/Center'
import { Heading } from '@astryxdesign/core/Heading'
import { Text } from '@astryxdesign/core/Text'
import { Button } from '@astryxdesign/core/Button'
import { IconButton } from '@astryxdesign/core/IconButton'
import { Icon } from '@astryxdesign/core/Icon'
import { Banner } from '@astryxdesign/core/Banner'
import { Spinner } from '@astryxdesign/core/Spinner'
import { StatusDot } from '@astryxdesign/core/StatusDot'
import { Token } from '@astryxdesign/core/Token'
import { List, ListItem } from '@astryxdesign/core/List'
import { MetadataList, MetadataListItem } from '@astryxdesign/core/MetadataList'
import { AlertDialog } from '@astryxdesign/core/AlertDialog'
import { Switch } from '@astryxdesign/core/Switch'
import { RadioList, RadioListItem } from '@astryxdesign/core/RadioList'
import AssigneePicker from './AssigneePicker'
import { readCourtBooking, cancelCourtGame, setCourtBlockAssignees, unblockCourt, updateCourtBlock } from './consoleApi'
import {
    SHOW_REASON_LABEL,
    assigneesDoneText,
    assigneesText,
    blockedByText,
    cancelDoneText,
    cancelQuestion,
    courtTitle,
    dayTitle,
    failureText,
    gameLineup,
    kindLabel,
    lessonKindText,
    lessonPlacesText,
    lessonTimeText,
    normalizeBooking,
    seatsText,
    showReasonDescription,
    showReasonDoneText,
    slotLook,
    slotTime,
    unblockDoneText,
} from './consoleText'

// The side panel of the day view: one booking, read fresh from the server
// when it opens, and what can be done about it. A game can be cancelled - its
// players are told by notification - and a block lifted, one slot or every
// slot the same "Bloquer…" created - and a block's reason shown to the club's
// members or hidden from them, for the whole block at once.
//
// A séance de coaching is shown and nothing more: its coach gives the court
// back from the app, which tells the players in it.
//
// Whatever it does, it hands the outcome back to the day view, which says it
// above the grid, closes the panel and reads the day again.
export default function CourtBookingPanel({ clubId, bookingId, onClose, onChanged, onUpdated, onConflict, onSessionLost }) {
    const [load, setLoad] = useState({ status: 'loading' })

    useEffect(() => {
        let isCurrent = true
        const read = async () => {
            let answer
            try {
                answer = await readCourtBooking(bookingId)
            } catch (e) {
                answer = { status: 0 }
            }
            if (!isCurrent) return
            if (answer.status === 200 && answer.json) {
                setLoad({ status: 'ready', booking: normalizeBooking(answer.json) })
            } else if (answer.status === 401) {
                onSessionLost()
            } else {
                setLoad({ status: 'error', title: failureText(answer.status) })
            }
        }
        read()
        return () => {
            isCurrent = false
        }
    }, [bookingId, onSessionLost])

    const booking = load.status === 'ready' ? load.booking : null
    const isGame = !!booking && booking.kind === 'COURT_BOOKING_KIND_GAME'
    const isBlock = !!booking && booking.state === 'CONSOLE_SLOT_STATE_BLOCKED'
    const isLesson = !!booking && booking.state === 'CONSOLE_SLOT_STATE_LESSON'
    let title = 'Créneau'
    if (isGame && booking.state !== 'CONSOLE_SLOT_STATE_HELD') title = 'Partie'
    else if (isBlock) title = 'Blocage'
    else if (isLesson) title = 'Cours'
    else if (booking) title = 'Réservé'

    const header = (
        <LayoutHeader hasDivider>
            <Stack direction="horizontal" gap={2} vAlign="center" hAlign="between">
                <Heading level={2}>{title}</Heading>
                <IconButton label="Fermer" variant="ghost" icon={<Icon icon="close" />} onClick={onClose} />
            </Stack>
        </LayoutHeader>
    )

    let body
    if (load.status === 'loading') {
        body = (
            <Center axis="both" padding={8}>
                <Spinner label="Chargement" />
            </Center>
        )
    } else if (load.status === 'error' || !booking) {
        body = <Banner status="error" title={load.title || failureText(0)} />
    } else if (isLesson) {
        body = <LessonDetail booking={booking} />
    } else if (isBlock) {
        body = (
            <BlockDetail
                clubId={clubId}
                booking={booking}
                onChanged={onChanged}
                onUpdated={onUpdated}
                onConflict={onConflict}
                onSessionLost={onSessionLost}
            />
        )
    } else if (isGame && booking.state !== 'CONSOLE_SLOT_STATE_HELD') {
        body = booking.game
            ? <GameDetail booking={booking} onChanged={onChanged} onConflict={onConflict} />
            : (
                <Stack gap={4}>
                    <Where booking={booking} />
                    <Banner
                        status="warning"
                        title="Partie introuvable"
                        description="Le créneau est réservé, mais la partie qui le tient n'existe plus."
                    />
                </Stack>
            )
    } else {
        body = (
            <Stack gap={4}>
                <Where booking={booking} />
                <Text color="secondary">Ce créneau est tenu par une réservation qui n'est ni une partie ni un blocage.</Text>
            </Stack>
        )
    }

    return (
        <Layout
            height="auto"
            padding={5}
            header={header}
            content={(
                <LayoutContent>
                    <Stack paddingBlockStart={4}>{body}</Stack>
                </LayoutContent>
            )}
        />
    )
}

CourtBookingPanel.propTypes = {
    clubId: PropTypes.string.isRequired,
    bookingId: PropTypes.string.isRequired,
    onClose: PropTypes.func.isRequired,
    onChanged: PropTypes.func.isRequired,
    // A change that keeps the panel open: said above the grid, which is read
    // again behind it.
    onUpdated: PropTypes.func.isRequired,
    onConflict: PropTypes.func.isRequired,
    onSessionLost: PropTypes.func.isRequired,
}

function capitalize(text) {
    return text.charAt(0).toUpperCase() + text.slice(1)
}

// Which court, which day, which time: the same three lines for every kind. A
// séance says when it really runs rather than the grid slot it was opened from.
function Where({ booking, time, children }) {
    return (
        <MetadataList label={{ width: 110 }}>
            <MetadataListItem label="Terrain">
                <Text>{courtTitle(booking)}</Text>
            </MetadataListItem>
            <MetadataListItem label="Date">
                <Text>{capitalize(dayTitle(booking))}</Text>
            </MetadataListItem>
            <MetadataListItem label="Heure">
                <Text hasTabularNumbers>{time || slotTime(booking.start_minute)}</Text>
            </MetadataListItem>
            {children}
        </MetadataList>
    )
}

Where.propTypes = {
    booking: PropTypes.object.isRequired,
    time: PropTypes.string,
    children: PropTypes.node,
}

function GameDetail({ booking, onChanged, onConflict }) {
    const [isConfirming, setConfirming] = useState(false)
    const [isCancelling, setCancelling] = useState(false)
    const { game } = booking
    const look = slotLook(booking)
    const lineup = gameLineup(game)
    const listedGuests = game.players.filter(player => player.is_guest).length

    const cancel = async () => {
        setCancelling(true)
        let answer
        try {
            answer = await cancelCourtGame(booking.id)
        } catch (e) {
            answer = { status: 0 }
        }
        setCancelling(false)
        setConfirming(false)
        if (answer.status === 200 && answer.json) {
            onChanged('success', cancelDoneText(Number(answer.json.notified) || 0))
        } else {
            onConflict(answer.status)
        }
    }

    return (
        <Stack gap={5}>
            <Stack direction="horizontal" gap={2} vAlign="center">
                <StatusDot variant={look.dot} label={look.text} />
                <Text weight="semibold">{look.text}</Text>
            </Stack>
            <Where booking={booking}>
                <MetadataListItem label="Organisateur">
                    <Text>{game.organiser_name || '—'}</Text>
                </MetadataListItem>
                <MetadataListItem label="Places">
                    <Text hasTabularNumbers>{seatsText(game)}</Text>
                </MetadataListItem>
                {game.created_by_club && (
                    <MetadataListItem label="Créée">
                        <Text>Par le club</Text>
                    </MetadataListItem>
                )}
            </Where>
            <Stack gap={2}>
                <Heading level={3}>Joueurs</Heading>
                {lineup.length === 0
                    ? <Text color="secondary">Aucun joueur pour l'instant.</Text>
                    : (
                        <List density="compact" hasDividers edgeCompensation="inline">
                            {game.players.map((player, index) => (
                                <ListItem
                                    key={player.user_id || `guest-${index}`}
                                    label={lineup[index]}
                                    description={playerDescription(player, game)}
                                />
                            ))}
                            {lineup.slice(game.players.length).map((name, index) => (
                                <ListItem key={`extra-${listedGuests + index}`} label={name} description="Invité" />
                            ))}
                        </List>
                    )}
            </Stack>
            {booking.is_past
                ? <Text type="supporting">La partie a commencé : elle ne peut plus être annulée.</Text>
                : (
                    <Stack direction="horizontal">
                        <Button label="Annuler la partie" variant="destructive" onClick={() => setConfirming(true)} />
                    </Stack>
                )}
            <AlertDialog
                isOpen={isConfirming}
                onOpenChange={setConfirming}
                title="Annuler la partie ?"
                description={cancelQuestion(booking)}
                actionLabel="Annuler la partie"
                cancelLabel="Garder la partie"
                isActionLoading={isCancelling}
                onAction={cancel}
                width={440}
            />
        </Stack>
    )
}

GameDetail.propTypes = {
    booking: PropTypes.object.isRequired,
    onChanged: PropTypes.func.isRequired,
    onConflict: PropTypes.func.isRequired,
}

// The line under a player's name: their handle when the name is shown,
// "Invité" for a guest, "Adhérent sans compte" for one the club put in from
// its roster, and who organised it.
function playerDescription(player, game) {
    const parts = []
    if (player.is_member) parts.push('Adhérent sans compte')
    else if (player.is_guest) parts.push('Invité')
    else if (player.full_name && player.username) parts.push(`@${player.username}`)
    if (player.user_id && player.user_id === game.organiser_id) parts.push('Organisateur')
    return parts.join(' · ') || undefined
}

function BlockDetail({ clubId, booking, onChanged, onUpdated, onConflict, onSessionLost }) {
    const [isConfirmingAll, setConfirmingAll] = useState(false)
    const [pending, setPending] = useState('') // '' | 'one' | 'all' | 'reason' | 'assignees'
    const [assignees, setAssignees] = useState(booking.assignees)
    // The list being edited, or null when it is only shown.
    const [draft, setDraft] = useState(null)
    const [scope, setScope] = useState('all')
    const [isShown, setShown] = useState(booking.show_reason)
    const look = slotLook(booking)
    const isGroup = booking.group_size > 1

    // Applies at once, to every slot of the block: one "Bloquer…" is one
    // thing to the admin, and a tournament half shown would be a surprise.
    // The panel stays open on the block; a failure puts the switch back.
    const changeReason = async show => {
        setShown(show)
        setPending('reason')
        let answer
        try {
            answer = await updateCourtBlock(booking.id, true, show)
        } catch (e) {
            answer = { status: 0 }
        }
        setPending('')
        if (answer.status === 200 && answer.json) {
            onUpdated('success', showReasonDoneText(show, Number(answer.json.updated) || 0))
        } else {
            setShown(!show)
            onConflict(answer.status)
        }
    }

    // The whole block by default, like the reason: the people on a
    // tournament are on all of it. One slot is there for the hour somebody
    // else stood in.
    const saveAssignees = async () => {
        setPending('assignees')
        let answer
        try {
            answer = await setCourtBlockAssignees(booking.id, isGroup && scope === 'all', draft.map(a => a.club_user_id))
        } catch (e) {
            answer = { status: 0 }
        }
        setPending('')
        if (answer.status === 200 && answer.json) {
            setAssignees(draft)
            setDraft(null)
            onUpdated('success', assigneesDoneText(Number(answer.json.updated) || 0))
        } else if (answer.status === 401) {
            onSessionLost()
        } else {
            onConflict(answer.status)
        }
    }

    const unblock = async wholeGroup => {
        setPending(wholeGroup ? 'all' : 'one')
        let answer
        try {
            answer = await unblockCourt(booking.id, wholeGroup)
        } catch (e) {
            answer = { status: 0 }
        }
        setPending('')
        setConfirmingAll(false)
        if (answer.status === 200 && answer.json) {
            onChanged('success', unblockDoneText(Number(answer.json.removed) || 0))
        } else {
            onConflict(answer.status)
        }
    }

    return (
        <Stack gap={5}>
            <Stack gap={1}>
                <Stack direction="horizontal">
                    <Token label={look.text} color={look.token} />
                </Stack>
                {booking.label && <Text weight="semibold" size="lg">{booking.label}</Text>}
            </Stack>
            <Where booking={booking}>
                <MetadataListItem label="Raison">
                    <Text>{kindLabel(booking.kind)}</Text>
                </MetadataListItem>
                {isGroup && (
                    <MetadataListItem label="Blocage">
                        <Text>{`${booking.group_size} créneaux`}</Text>
                    </MetadataListItem>
                )}
                <MetadataListItem label="Intervenants">
                    <Text>{assigneesText(assignees)}</Text>
                </MetadataListItem>
            </Where>
            <Text type="supporting">{blockedByText(booking.created_by_name, booking.created_at)}</Text>
            {draft ? (
                <Stack gap={3}>
                    <AssigneePicker clubId={clubId} value={draft} onChange={setDraft} onSessionLost={onSessionLost} />
                    {isGroup && (
                        <RadioList label="Appliquer à" value={scope} onChange={setScope}>
                            <RadioListItem value="all" label={`Tout le blocage (${booking.group_size} créneaux)`} />
                            <RadioListItem value="one" label="Ce créneau seulement" />
                        </RadioList>
                    )}
                    <Stack direction="horizontal" gap={2}>
                        <Button label="Annuler" variant="secondary" isDisabled={pending === 'assignees'} onClick={() => setDraft(null)} />
                        <Button label="Enregistrer" variant="primary" isLoading={pending === 'assignees'} onClick={saveAssignees} />
                    </Stack>
                </Stack>
            ) : (
                <Stack hAlign="start">
                    <Button
                        label="Modifier les intervenants"
                        variant="secondary"
                        isDisabled={pending !== ''}
                        onClick={() => {
                            setScope('all')
                            setDraft(assignees)
                        }}
                    />
                </Stack>
            )}
            <Switch
                label={SHOW_REASON_LABEL}
                value={isShown}
                onChange={changeReason}
                isLoading={pending === 'reason'}
                isDisabled={pending === 'one' || pending === 'all'}
                description={showReasonDescription(booking.kind, booking.label, isShown)}
            />
            <Stack gap={2} hAlign="start">
                <Button
                    label="Débloquer"
                    variant="secondary"
                    isLoading={pending === 'one'}
                    isDisabled={pending === 'all' || pending === 'reason'}
                    onClick={() => unblock(false)}
                />
                {isGroup && (
                    <Button
                        label={`Débloquer tout le blocage (${booking.group_size} créneaux)`}
                        variant="secondary"
                        isDisabled={pending !== ''}
                        onClick={() => setConfirmingAll(true)}
                    />
                )}
            </Stack>
            <AlertDialog
                isOpen={isConfirmingAll}
                onOpenChange={setConfirmingAll}
                title="Débloquer tout le blocage ?"
                description={`Les ${booking.group_size} créneaux de ce blocage redeviendront libres et réservables par les joueurs.`}
                actionLabel={`Débloquer ${booking.group_size} créneaux`}
                actionVariant="primary"
                cancelLabel="Annuler"
                isActionLoading={pending === 'all'}
                onAction={() => unblock(true)}
                width={440}
            />
        </Stack>
    )
}

BlockDetail.propTypes = {
    clubId: PropTypes.string.isRequired,
    onSessionLost: PropTypes.func.isRequired,
    booking: PropTypes.object.isRequired,
    onChanged: PropTypes.func.isRequired,
    onUpdated: PropTypes.func.isRequired,
    onConflict: PropTypes.func.isRequired,
}

function LessonDetail({ booking }) {
    const { lesson } = booking
    const look = slotLook(booking)

    return (
        <Stack gap={5}>
            <Stack gap={1}>
                <Stack direction="horizontal">
                    <Token label={lessonKindText(lesson)} color={look.token} />
                </Stack>
                {lesson && lesson.label && <Text weight="semibold" size="lg">{lesson.label}</Text>}
            </Stack>
            <Where booking={booking} time={lessonTimeText(lesson)}>
                <MetadataListItem label="Coach">
                    <Text>{(lesson && lesson.coach_name) || booking.created_by_name || '—'}</Text>
                </MetadataListItem>
                {lesson && (
                    <MetadataListItem label="Joueurs">
                        <Text hasTabularNumbers>{lessonPlacesText(lesson)}</Text>
                    </MetadataListItem>
                )}
            </Where>
            <Text type="supporting">
                Terrain réservé par le coach pour son cours. Seul le coach peut le libérer, depuis l'app Padelr : ses joueurs sont alors prévenus.
            </Text>
        </Stack>
    )
}

LessonDetail.propTypes = {
    booking: PropTypes.object.isRequired,
}
